import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getAllOrders, shipOrder, completeOrder, getOrderItems } from "@/services/supabaseService";
import type { Order, OrderItem } from "@/lib/supabase";
import AdminLayout from "@/components/feature/AdminLayout";

const STATUS_DISPLAY_MAP: Record<string, string> = {
  "待付款": "待付款",
  "待發貨": "已訂貨",
  "已發貨": "已上架",
  "已完成": "銷售完畢",
  "已取消": "已取消",
};

export default function OrderManage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [filterStatus, setFilterStatus] = useState<"all" | "待發貨" | "已發貨" | "已完成">("all");
  const [trackingModal, setTrackingModal] = useState<Order | null>(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [confirmModal, setConfirmModal] = useState<{
    order: Order;
    profit: number;
    items: OrderItem[];
  } | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await getAllOrders();
      setOrders(data);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">返回首頁</button>
        </div>
      </div>
    );
  }

  const filtered = filterStatus === "all" ? orders : orders.filter((o) => o.status === filterStatus);

  const handleShip = async (orderId: string, tracking: string) => {
    try {
      await shipOrder(orderId, tracking);
      setTrackingModal(null);
      setTrackingNumber("");
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "發貨失敗");
    }
  };

  const handleComplete = async (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    try {
      const items = await getOrderItems(orderId);
      const refund = items.reduce((sum, item) => sum + Number(item.suggested_price) * item.quantity, 0);
      setConfirmModal({ order, profit: refund, items });
    } catch {
      // silently fail
    }
  };

  const confirmCompleteOrder = async (orderId: string) => {
    try {
      await completeOrder(orderId);
      setConfirmModal(null);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "操作失敗");
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "待發貨").length,
    shipped: orders.filter((o) => o.status === "已發貨").length,
    completed: orders.filter((o) => o.status === "已完成").length,
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "待發貨": return "bg-amber-100 text-amber-700";
      case "已發貨": return "bg-blue-100 text-blue-700";
      case "已完成": return "bg-emerald-100 text-emerald-700";
      case "已取消": return "bg-gray-100 text-gray-500";
      default: return "bg-gray-100 text-gray-500";
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">訂單與發貨管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">查看分銷訂單，管理發貨狀態並回填物流單號</p>
          </div>
          <button
            onClick={() => navigate("/admin/members")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-700 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer shadow-sm"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-team-line text-xs"></i>
            </div>
            會員管理
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "全部訂單", value: stats.total, color: "bg-gray-100 text-gray-700", status: "all" as const },
            { label: "待發貨", value: stats.pending, color: "bg-amber-100 text-amber-700", status: "待發貨" as const },
            { label: "已發貨", value: stats.shipped, color: "bg-blue-100 text-blue-700", status: "已發貨" as const },
            { label: "已完成", value: stats.completed, color: "bg-emerald-100 text-emerald-700", status: "已完成" as const },
          ].map((s) => (
            <button
              key={s.label}
              onClick={() => setFilterStatus(s.status)}
              className={`rounded-xl p-4 text-center transition-all cursor-pointer shadow-sm ${s.color} ${
                filterStatus === s.status ? "ring-2 ring-amber-400" : ""
              }`}
            >
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12 text-gray-400 text-sm">載入中...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">訂單編號</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">分銷商</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">金額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">狀態</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">物流單號</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{order.id}</p>
                        <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleString("zh-TW")}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-800">{order.user_name}</p>
                      </td>
                      <td className="px-4 py-3 font-bold text-amber-600">${Number(order.total_amount).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyle(order.status)}`}>
                          {STATUS_DISPLAY_MAP[order.status] || order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {order.tracking_number ? (
                          <span className="text-xs text-gray-600 font-mono">{order.tracking_number}</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {order.status === "待發貨" && (
                          <button
                            onClick={() => { setTrackingModal(order); setTrackingNumber(""); }}
                            className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 cursor-pointer"
                          >
                            填寫物流
                          </button>
                        )}
                        {order.status === "已發貨" && (
                          <button
                            onClick={() => handleComplete(order.id)}
                            className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 cursor-pointer"
                          >
                            確認銷售完畢
                          </button>
                        )}
                        {order.status === "已完成" && (
                          <span className="text-xs text-emerald-600 inline-flex items-center gap-1">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-check-double-line text-[10px]"></i>
                            </div>
                            銷售完畢
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">暫無訂單</div>
          )}
        </div>
      </div>

      {/* Confirm Complete Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">確認銷售完畢</h3>
            <p className="text-sm text-gray-500 mb-4">
              {confirmModal.order.id} · {confirmModal.order.user_name}
            </p>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-coins-line text-emerald-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-emerald-700">全額返款 (含本金與利潤)</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">${confirmModal.profit}</p>
              <p className="text-xs text-emerald-600 mt-1">
                是否將全額返還至 <span className="font-medium">{confirmModal.order.user_name}</span> 的錢包？
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => confirmCompleteOrder(confirmModal.order.id)}
                className="flex-1 py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 cursor-pointer"
              >
                確認返還
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {trackingModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">填寫物流單號</h3>
            <p className="text-sm text-gray-500 mb-4">{trackingModal.id} · {trackingModal.user_name}</p>
            <label className="text-sm text-gray-600 mb-1.5 block">物流單號</label>
            <input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="例如：SF1234567890"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setTrackingModal(null); setTrackingNumber(""); }}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => handleShip(trackingModal.id, trackingNumber)}
                disabled={!trackingNumber.trim()}
                className="flex-1 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
              >
                確認發貨
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}